/**
 * Build a Live Training session plan from the coach/group last-month history:
 * most common exercise types → blocks; per type propose done + never-done picks.
 * Also: compact “similar to last session” context for Pingflo (same types, new drills).
 */

import moment from "moment";
import { localizedExerciseName, localizedTypeName } from "./localizedCatalogFields";
import {
  sessionlist,
  sessionexlist,
  getexercise,
} from "../components/funtionsBD/Mainservice";
import { isPlaceholderBlock, isTypeOnlyBlock, plannedBlockTitle, exerciseLabel } from "./planningSubgroups";
import { planningExerciseVoiceHints } from "./planningVoiceCommands";

const MAX_SESSIONS = 24;
const MAX_TYPES = 6;
const PAGE_SIZE = 3;
const SIMILAR_ALT_PER_TYPE = 4;

function normalizeToken(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function exerciseName(row, lng = "en") {
  const nested = row?.tbl_exercise || row?.exercise || row;
  return localizedExerciseName(nested, lng) || localizedExerciseName(row, lng);
}

function extractExerciseRef(teRow, lng = "en") {
  const nested = teRow?.tbl_exercise || teRow?.exercise || null;
  const exId = nested?.ex_id || teRow?.ex_id || null;
  if (!exId) return null;
  const type =
    nested?.tbl_type_ex ||
    nested?.type_ex ||
    teRow?.type_ex ||
    teRow?.sub_type_ex_direct?.tbl_type_ex ||
    null;
  const typeId =
    type?.type_id ||
    nested?.type_id ||
    teRow?.type_id ||
    teRow?.sub_type_ex_direct?.type_id ||
    null;
  if (!typeId) return null;
  const sub =
    nested?.tbl_sub_type_ex ||
    nested?.sub_type_ex ||
    teRow?.sub_type_ex ||
    teRow?.sub_type_ex_direct ||
    null;
  const duration =
    parseInt(teRow?.te_duration, 10) ||
    parseInt(nested?.duration, 10) ||
    parseInt(nested?.ex_duration, 10) ||
    10;
  const typeName = localizedTypeName(type, lng);
  return {
    ex_id: String(exId),
    type_id: String(typeId),
    type_name: typeName,
    sub_type_id: sub?.sub_type_id ? String(sub.sub_type_id) : null,
    name: exerciseName(teRow, lng),
    duration: Number.isFinite(duration) && duration > 0 ? duration : 10,
  };
}

function tokenize(name) {
  return normalizeToken(name)
    .split(" ")
    .filter((t) => t.length > 2);
}

function closenessScore(candidate, doneRefs) {
  if (!doneRefs.length) return 0;
  let score = 0;
  const candTokens = new Set(tokenize(candidate.name));
  const subCounts = new Map();
  for (const done of doneRefs) {
    if (done.sub_type_id) {
      subCounts.set(done.sub_type_id, (subCounts.get(done.sub_type_id) || 0) + 1);
    }
    for (const tok of tokenize(done.name)) {
      if (candTokens.has(tok)) score += 2;
    }
  }
  if (candidate.sub_type_id && subCounts.has(candidate.sub_type_id)) {
    score += 20 + subCounts.get(candidate.sub_type_id);
  }
  return score;
}

function sessionSortKey(session) {
  const raw =
    session?.ts_date ||
    session?.session_date ||
    session?.ts_start ||
    session?.createdAt ||
    "";
  const m = moment(raw);
  return m.isValid() ? m.valueOf() : 0;
}

/**
 * Rolling last 30 days window ending yesterday (exclude in-progress today session).
 */
export function lastMonthDateWindow(now = new Date()) {
  const end = moment(now).subtract(1, "day").endOf("day");
  const start = moment(now).subtract(30, "days").startOf("day");
  return {
    date_from: start.format("YYYY-MM-DD"),
    date_to: end.format("YYYY-MM-DD"),
  };
}

/**
 * Compact context for Pingflo: last session blocks (types + exercises) and
 * alternative catalogue drills of the same types (prefer never-done / close variants).
 * Used when the coach asks for a similar session to the previous one.
 */
export async function buildSimilarToLastSessionContext({
  grId,
  excludeTsId = null,
  language = "en",
  altPerType = SIMILAR_ALT_PER_TYPE,
} = {}) {
  if (!grId) {
    return { available: false, reason: "missing_group" };
  }
  const { date_from, date_to } = lastMonthDateWindow();
  const listRes = await sessionlist({
    gr_id: grId,
    date_from,
    date_to,
    include_hr: false,
  });
  const sessions = (listRes?.data?.data || [])
    .filter((s) => s?.ts_id && String(s.ts_id) !== String(excludeTsId || ""))
    .sort((a, b) => sessionSortKey(b) - sessionSortKey(a));

  const last = sessions[0] || null;
  if (!last?.ts_id) {
    return {
      available: false,
      reason: "no_prior_session",
      date_from,
      date_to,
    };
  }

  const exRes = await sessionexlist({ ts_id: last.ts_id });
  const rows = Array.isArray(exRes?.data?.data) ? exRes.data.data : [];
  const lastBlocks = [];
  const byType = new Map();

  for (const row of rows) {
    const ref = extractExerciseRef(row, language);
    if (!ref) continue;
    lastBlocks.push({
      type_id: ref.type_id,
      type_name: ref.type_name || ref.type_id,
      exercise_name: ref.name || ref.ex_id,
      ex_id: ref.ex_id,
      duration_minutes: ref.duration,
      sub_type_id: ref.sub_type_id,
    });
    if (!byType.has(ref.type_id)) {
      byType.set(ref.type_id, {
        type_id: ref.type_id,
        type_name: ref.type_name || ref.type_id,
        last_exercises: [],
      });
    }
    const bucket = byType.get(ref.type_id);
    if (!bucket.type_name && ref.type_name) bucket.type_name = ref.type_name;
    bucket.last_exercises.push(ref);
  }

  if (!lastBlocks.length) {
    return {
      available: false,
      reason: "last_session_empty",
      last_ts_id: String(last.ts_id),
      last_date: last.ts_date || last.session_date || last.ts_start || null,
    };
  }

  const type_alternatives = [];
  for (const typeRow of byType.values()) {
    const usedIds = new Set(typeRow.last_exercises.map((e) => e.ex_id));
    let alternatives = [];
    try {
      const catalogRes = await getexercise({
        type_id: typeRow.type_id,
        group_id: grId || null,
      });
      const catalog = (catalogRes?.data?.data || [])
        .map((row) => {
          const exId = row?.ex_id;
          if (!exId || usedIds.has(String(exId))) return null;
          const name = exerciseName(row, language);
          const duration =
            parseInt(row.duration, 10) ||
            parseInt(row.ex_duration, 10) ||
            typeRow.last_exercises[0]?.duration ||
            10;
          return {
            ex_id: String(exId),
            type_id: String(typeRow.type_id),
            type_name: typeRow.type_name,
            sub_type_id: row?.sub_type_id
              ? String(row.sub_type_id)
              : row?.sub_type_ex?.sub_type_id
                ? String(row.sub_type_ex.sub_type_id)
                : null,
            name,
            duration: Number.isFinite(duration) && duration > 0 ? duration : 10,
          };
        })
        .filter(Boolean);
      alternatives = catalog
        .map((c) => ({
          ...c,
          _score: closenessScore(c, typeRow.last_exercises),
        }))
        .sort(
          (a, b) =>
            b._score - a._score || String(a.name).localeCompare(String(b.name))
        )
        .slice(0, Math.max(1, altPerType))
        .map(({ _score, ...rest }) => ({
          name: rest.name,
          duration_minutes: rest.duration,
          ex_id: rest.ex_id,
        }));
    } catch {
      alternatives = [];
    }
    type_alternatives.push({
      type_id: typeRow.type_id,
      type_name: typeRow.type_name,
      last_exercise_names: typeRow.last_exercises.map(
        (e) => e.name || e.ex_id
      ),
      alternative_exercises: alternatives,
    });
  }

  const lastDate = last.ts_date || last.session_date || last.ts_start || null;

  return {
    available: true,
    last_ts_id: String(last.ts_id),
    last_date: lastDate,
    last_session_label: lastDate
      ? `session on ${moment(lastDate).format("YYYY-MM-DD")}`
      : "previous session",
    last_blocks: lastBlocks.map((b) => ({
      type_name: b.type_name,
      exercise_name: b.exercise_name,
      duration_minutes: b.duration_minutes,
    })),
    type_names: Array.from(
      new Set(lastBlocks.map((b) => b.type_name).filter(Boolean))
    ),
    type_alternatives,
  };
}

/**
 * Compact snapshot of exercises already on today's live session plan.
 * Sent to Digiping/Pingflo so plan-assist can refine instead of redesigning.
 */
export function buildCurrentSessionPlanContext(
  exercises = [],
  { language = "en", stations = [] } = {}
) {
  const stationNameByTsg = new Map();
  for (const station of stations || []) {
    if (station?.tsg_id == null) continue;
    const name =
      station.name ||
      station.tsg_name ||
      station.station_name ||
      station.label ||
      null;
    stationNameByTsg.set(String(station.tsg_id), name);
  }

  const blocks = (exercises || [])
    .map((ex, index) => {
      const typeOnly = isTypeOnlyBlock(ex);
      if (isPlaceholderBlock(ex) && !typeOnly) return null;
      const ref = extractExerciseRef(ex, language);
      const tsgId = ex?.tsg_id != null ? String(ex.tsg_id) : null;
      const name =
        (ref?.name || "").trim() ||
        plannedBlockTitle(ex, language) ||
        exerciseLabel(ex, language) ||
        String(ex?.ex_name_en || ex?.ex_name || "").trim();
      if (!name) return null;
      const duration =
        ref?.duration ||
        parseInt(ex?.te_duration, 10) ||
        parseInt(ex?.duration, 10) ||
        null;
      return {
        te_id: ex?.te_id != null ? String(ex.te_id) : null,
        te_order:
          ex?.te_order != null && ex.te_order !== ""
            ? Number(ex.te_order)
            : index + 1,
        te_wave:
          ex?.te_wave != null && ex.te_wave !== ""
            ? Number(ex.te_wave)
            : null,
        tsg_id: tsgId,
        station_name: tsgId ? stationNameByTsg.get(tsgId) || null : null,
        exercise_name: name,
        type_name:
          ref?.type_name ||
          ex?.type_ex?.type_name_en ||
          ex?.tbl_type_ex?.type_name_en ||
          "",
        type_id:
          ref?.type_id ||
          (ex?.type_id != null
            ? String(ex.type_id)
            : ex?.type_ex?.type_id != null
              ? String(ex.type_ex.type_id)
              : null),
        ex_id: ref?.ex_id || (ex?.ex_id != null ? String(ex.ex_id) : null),
        type_only: typeOnly,
        duration_minutes:
          Number.isFinite(duration) && duration > 0 ? duration : null,
      };
    })
    .filter(Boolean)
    .slice(0, 40);

  return {
    available: blocks.length > 0,
    block_count: blocks.length,
    planning_exercise_names: planningExerciseVoiceHints(exercises, language),
    current_session_blocks: blocks,
  };
}

/**
 * Aggregate coach/group history into typed session blocks with done/new picks.
 */
export async function buildCoachHistorySessionPlan({
  grId,
  excludeTsId = null,
  language = "en",
  targetDuration = 90,
  maxTypes = MAX_TYPES,
} = {}) {
  if (!grId) {
    throw new Error("gr_id is required");
  }
  const { date_from, date_to } = lastMonthDateWindow();
  const listRes = await sessionlist({
    gr_id: grId,
    date_from,
    date_to,
    include_hr: false,
  });
  const sessions = (listRes?.data?.data || [])
    .filter((s) => s?.ts_id && String(s.ts_id) !== String(excludeTsId || ""))
    .slice(0, MAX_SESSIONS);

  const typeStats = new Map();
  const exerciseStats = new Map();

  await Promise.all(
    sessions.map(async (session) => {
      try {
        const exRes = await sessionexlist({ ts_id: session.ts_id });
        const rows = exRes?.data?.data || [];
        for (const row of rows) {
          const ref = extractExerciseRef(row, language);
          if (!ref) continue;
          const tKey = ref.type_id;
          if (!typeStats.has(tKey)) {
            typeStats.set(tKey, {
              type_id: ref.type_id,
              type_name: ref.type_name,
              count: 0,
              durationSum: 0,
              durationN: 0,
            });
          }
          const t = typeStats.get(tKey);
          t.count += 1;
          t.durationSum += ref.duration;
          t.durationN += 1;
          if (!t.type_name && ref.type_name) t.type_name = ref.type_name;

          const eKey = `${ref.type_id}::${ref.ex_id}`;
          if (!exerciseStats.has(eKey)) {
            exerciseStats.set(eKey, { ...ref, count: 0 });
          }
          const e = exerciseStats.get(eKey);
          e.count += 1;
          e.duration = Math.round((e.duration + ref.duration) / 2) || ref.duration;
          if (!e.name && ref.name) e.name = ref.name;
          if (!e.sub_type_id && ref.sub_type_id) e.sub_type_id = ref.sub_type_id;
        }
      } catch {
        /* skip broken sessions */
      }
    })
  );

  const typesRanked = Array.from(typeStats.values()).sort(
    (a, b) => b.count - a.count
  );
  const slotBudget = Math.max(
    1,
    Math.min(
      maxTypes,
      Math.max(typesRanked.length ? 1 : 0, Math.round(Number(targetDuration) / 15) || 4)
    )
  );
  const selectedTypes = typesRanked.slice(0, slotBudget);

  const blocks = [];
  for (const typeRow of selectedTypes) {
    const doneForType = Array.from(exerciseStats.values())
      .filter((e) => e.type_id === typeRow.type_id)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    const doneIds = new Set(doneForType.map((e) => e.ex_id));
    let never = [];
    try {
      const catalogRes = await getexercise({
        type_id: typeRow.type_id,
        group_id: grId || null,
      });
      const catalog = (catalogRes?.data?.data || [])
        .map((row) => {
          const exId = row?.ex_id;
          if (!exId || doneIds.has(String(exId))) return null;
          const name = exerciseName(row, language);
          const duration =
            parseInt(row.duration, 10) ||
            parseInt(row.ex_duration, 10) ||
            Math.round(typeRow.durationSum / Math.max(1, typeRow.durationN)) ||
            10;
          return {
            ex_id: String(exId),
            type_id: String(typeRow.type_id),
            type_name: typeRow.type_name,
            sub_type_id: row?.sub_type_id
              ? String(row.sub_type_id)
              : row?.sub_type_ex?.sub_type_id
                ? String(row.sub_type_ex.sub_type_id)
                : null,
            name,
            duration: Number.isFinite(duration) && duration > 0 ? duration : 10,
            count: 0,
            never_done: true,
          };
        })
        .filter(Boolean);
      never = catalog
        .map((c) => ({
          ...c,
          _score: closenessScore(c, doneForType.slice(0, 5)),
        }))
        .sort(
          (a, b) =>
            b._score - a._score || String(a.name).localeCompare(String(b.name))
        )
        .map(({ _score, ...rest }) => rest);
    } catch {
      never = [];
    }

    const avgDuration =
      Math.round(typeRow.durationSum / Math.max(1, typeRow.durationN)) || 10;

    blocks.push({
      type_id: typeRow.type_id,
      type_name: typeRow.type_name || typeRow.type_id,
      times_used: typeRow.count,
      duration_minutes: avgDuration,
      done: doneForType.map((e) => ({
        ex_id: e.ex_id,
        name: e.name || e.ex_id,
        duration: e.duration,
        count: e.count,
        type_id: e.type_id,
        never_done: false,
      })),
      never: never.slice(0, 6).map((e) => ({
        ex_id: e.ex_id,
        name: e.name || e.ex_id,
        duration: e.duration,
        count: 0,
        type_id: e.type_id,
        never_done: true,
      })),
    });
  }

  return {
    session_count: sessions.length,
    date_from,
    date_to,
    blocks,
  };
}

/**
 * Visible picks for a block: first page = 3 done + 3 never;
 * expanded = +3 done + +3 never.
 */
export function visiblePicksForBlock(block, expanded = false) {
  const done = Array.isArray(block?.done) ? block.done : [];
  const never = Array.isArray(block?.never) ? block.never : [];
  const doneTake = expanded ? PAGE_SIZE * 2 : PAGE_SIZE;
  const neverTake = expanded ? PAGE_SIZE * 2 : PAGE_SIZE;
  const picks = [
    ...done.slice(0, doneTake).map((e) => ({ ...e, bucket: "done" })),
    ...never.slice(0, neverTake).map((e) => ({ ...e, bucket: "never" })),
  ];
  const hasMore = done.length > PAGE_SIZE || never.length > PAGE_SIZE;
  return { picks, hasMore, canExpand: hasMore && !expanded };
}

export const COACH_HISTORY_PAGE_SIZE = PAGE_SIZE;

/** Test helpers */
export const __test__ = {
  extractExerciseRef,
  closenessScore,
  normalizeToken,
  sessionSortKey,
  PAGE_SIZE,
};
